import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {
    NatAbsenceContactAddress,
    NatContactAddressOnEmergency,
    NatFamilyStructure,
    NatRentAdminContactAddress,
    NatResidentContactAddressOnEmergencyLocationState,
    NatWorkAddress,
} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatResidentContactAddressOnEmergencyForm from './resident/NatResidentContactAddressOnEmergencyForm'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import {HAVE_OR_NOT, RESIDENCE_STYLE} from '../../../../CodeMaster'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {
    NatPutContactAddressOnEmergencyRequest,
    NatPutContactAddressOnEmergencyResponse,
} from '../repository/NatPutContactAddressOnEmergency'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentContactAddressOnEmergencyLocationState>
}

interface State {
    residenceStyle: Nullable<string>
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    occupancyDate: Nullable<string>
    contactAddressOnEmergency: Nullable<NatContactAddressOnEmergency>
    absenceContactAddress: Nullable<NatAbsenceContactAddress>
    rentAdminContactAddressFlag: Nullable<string>
    rentAdminContactAddress: Nullable<NatRentAdminContactAddress>
    familyStructureList: Nullable<NatFamilyStructure[]>
}

class NatResidentContactAddressOnEmergencyInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            residenceStyle: null,
            applicantFamilyName: null,
            applicantFirstName: null,
            occupancyDate: null,
            contactAddressOnEmergency: null,
            absenceContactAddress: null,
            rentAdminContactAddressFlag: null,
            rentAdminContactAddress: null,
            familyStructureList: null,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {applyInfo, residenceStyle} = this.props.location.state

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultOccupancyDate: Nullable<string> = null
        let defaultContactAddressOnEmergency: Nullable<NatContactAddressOnEmergency> = null
        let defaultAbsenceContactAddress: Nullable<NatAbsenceContactAddress> = null
        let defaultRentAdminContactAddressFlag: Nullable<string> = null
        let defaultRentAdminContactAddress: Nullable<NatRentAdminContactAddress> = null
        let defaultFamilyStructureList: Nullable<NatFamilyStructure[]> = null

        if (applyInfo) {
            const {
                occupancyDate, contactAddressOnEmergency, absenceContactAddress, rentAdminContactAddress,
                rentAdminContactAddressFlag, familyStructureList,
            } = applyInfo

            if (residenceStyle) {
                switch (residenceStyle) {
                    case RESIDENCE_STYLE.UNIT_OWNER.value:
                        defaultOccupancyDate = occupancyDate ?? defaultOccupancyDate
                        defaultContactAddressOnEmergency = contactAddressOnEmergency ?? defaultContactAddressOnEmergency
                        defaultAbsenceContactAddress = absenceContactAddress ?? defaultAbsenceContactAddress
                        defaultFamilyStructureList = familyStructureList ?? defaultFamilyStructureList
                        break
                    case RESIDENCE_STYLE.APPROPRIATOR_AND_TENANT.value:
                        defaultOccupancyDate = occupancyDate ?? defaultOccupancyDate
                        defaultContactAddressOnEmergency = contactAddressOnEmergency ?? defaultContactAddressOnEmergency
                        defaultAbsenceContactAddress = absenceContactAddress ?? defaultAbsenceContactAddress
                        defaultRentAdminContactAddressFlag = rentAdminContactAddressFlag ?? HAVE_OR_NOT.HAVE.value
                        defaultRentAdminContactAddress = rentAdminContactAddress ?? defaultRentAdminContactAddress
                        defaultFamilyStructureList = familyStructureList ?? defaultFamilyStructureList
                        break
                    case RESIDENCE_STYLE.EXTERNAL_UNIT_OWNER.value:
                        defaultContactAddressOnEmergency = contactAddressOnEmergency ?? defaultContactAddressOnEmergency
                        defaultAbsenceContactAddress = absenceContactAddress ?? defaultAbsenceContactAddress
                        defaultRentAdminContactAddressFlag = rentAdminContactAddressFlag ?? HAVE_OR_NOT.HAVE.value
                        defaultRentAdminContactAddress = rentAdminContactAddress ?? defaultRentAdminContactAddress
                        break
                }
            }
        }

        this.setState({
            residenceStyle: residenceStyle,
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            occupancyDate: defaultOccupancyDate,
            contactAddressOnEmergency: defaultContactAddressOnEmergency,
            absenceContactAddress: defaultAbsenceContactAddress,
            rentAdminContactAddressFlag: defaultRentAdminContactAddressFlag,
            rentAdminContactAddress: defaultRentAdminContactAddress,
            familyStructureList: defaultFamilyStructureList,
        })
    }

    async onClickApplyConfirmButton(request: NatPutContactAddressOnEmergencyRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutContactAddressOnEmergencyResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyContactAddressOnEmergency.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const contactAddressOnEmergencyLocationState: NatContactAddressOnEmergency = {
                    familyName: request.contactAddressOnEmergency.familyName ?? '',
                    firstName: request.contactAddressOnEmergency.firstName ?? '',
                    zipCode: request.contactAddressOnEmergency.zipCode ?? '',
                    prefecture: request.contactAddressOnEmergency.prefecture ?? '',
                    address2: request.contactAddressOnEmergency.address2 ?? '',
                    address3: request.contactAddressOnEmergency.address3 ?? '',
                    buildingRoomNumber: request.contactAddressOnEmergency.buildingRoomNumber ?? '',
                    fixedPhoneNumber: request.contactAddressOnEmergency.fixedPhoneNumber ?? '',
                    mobilePhoneNumber: request.contactAddressOnEmergency.mobilePhoneNumber ?? '',
                }

                const absenceContactAddressLocationState: NatAbsenceContactAddress = {
                    familyName: request.absenceContactAddress.familyName ?? '',
                    firstName: request.absenceContactAddress.firstName ?? '',
                    zipCode: request.absenceContactAddress.zipCode ?? '',
                    prefecture: request.absenceContactAddress.prefecture ?? '',
                    address2: request.absenceContactAddress.address2 ?? '',
                    address3: request.absenceContactAddress.address3 ?? '',
                    buildingRoomNumber: request.absenceContactAddress.buildingRoomNumber ?? '',
                    fixedPhoneNumber: request.absenceContactAddress.fixedPhoneNumber ?? '',
                    mobilePhoneNumber: request.absenceContactAddress.mobilePhoneNumber ?? '',
                }

                const rentAdminContactAddressLocationState: NatRentAdminContactAddress = {
                    name: request.rentAdminContactAddress?.name ?? '',
                    zipCode: request.rentAdminContactAddress?.zipCode ?? '',
                    prefecture: request.rentAdminContactAddress?.prefecture ?? '',
                    address2: request.rentAdminContactAddress?.address2 ?? '',
                    address3: request.rentAdminContactAddress?.address3 ?? '',
                    buildingRoomNumber: request.rentAdminContactAddress?.buildingRoomNumber ?? '',
                    phoneNumber: request.rentAdminContactAddress?.phoneNumber ?? '',
                }

                let familyStructureRequest: NatFamilyStructure[] = []
                if (request.familyStructureList) {
                    familyStructureRequest = request.familyStructureList.map(family => {
                        const birthSplit = family.birthDate.split('-')
                        const workAddressLocationState: NatWorkAddress = {
                            name: family.workAddress.name ?? '',
                            zipCode: family.workAddress.zipCode ?? '',
                            prefecture: family.workAddress.prefecture ?? '',
                            address2: family.workAddress.address2 ?? '',
                            address3: family.workAddress.address3 ?? '',
                            buildingRoomNumber: family.workAddress.buildingRoomNumber ?? '',
                            phoneNumber: family.workAddress.phoneNumber ?? '',
                        }

                        return {
                            index: family.index,
                            relationship: family.relationship,
                            familyName: family.familyName,
                            firstName: family.firstName,
                            familyNameKana: family.familyNameKana,
                            firstNameKana: family.firstNameKana,
                            birthDate: family.birthDate,
                            birthYear: birthSplit[0] ?? '',
                            birthMonth: birthSplit[1] ?? '',
                            birthDay: birthSplit[2] ?? '',
                            workAddress: workAddressLocationState,
                        }
                    })
                }

                const locationState: NatResidentContactAddressOnEmergencyLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        occupancyDate: (request.occupancyDate) ?? '',
                        contactAddressOnEmergency: contactAddressOnEmergencyLocationState,
                        absenceContactAddress: absenceContactAddressLocationState,
                        rentAdminContactAddressFlag: request.rentAdminContactAddressFlag ?? '',
                        rentAdminContactAddress: rentAdminContactAddressLocationState,
                        familyStructureList: (familyStructureRequest.length > 0)
                            ? familyStructureRequest : [],
                    },
                    applyId: this.props.location.state.applyId,
                    residenceStyle: this.props.location.state.residenceStyle,
                }

                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/contactAddressOnEmergencies/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        return (
            <>
                <PlainBackLink
                    to={this.props.location.state.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/contactAddressOnEmergencies/${this.props.location.state.applyId}/copy`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications/contactAddressOnEmergencies/agreement`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    緊急時連絡表
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentContactAddressOnEmergencyForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentContactAddressOnEmergencyInputPage)
